import { useEffect, useMemo, useState } from 'react';
import { Divider, Skeleton } from '@mui/material';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '../../auth/AuthProvider';
import { format, addDays, parseISO, isValid } from 'date-fns';

import PageHeaderComponent from './components/PageHeaderComponent';
import RoomInfoComponent from './components/RoomInfoComponent';
import TripBenefit from './components/TripBenefit';
import TripInfoComponent from './components/TripInfoComponent';
import GuestDetailForm from './components/GuestDetailForm';
import CheckoutForm from './components/StripComponent';
import PayLaterForm from './components/PayLaterForm';

import useGetTripBenefits from '../../hooks/useGetTripBenefit';
import useGetUser from '../../hooks/useGetUser';
import useCreatePaymentIntent from '../../hooks/useCreatePaymentIntent';

import {
  PaymentIntentRoomInput,
  PaymentIntentCreateInput,
} from '../../gql/graphql';
import { computeNumberOfGuests } from '../../utils/guestCount';
import { paymentIntentIdFromClientSecret } from '../../utils/paymentIntent';
import {
  loadPaymentDraft,
  savePaymentDraft,
  type PaymentLocationState,
} from '../../utils/paymentDraft';

const PAYMENT_OPTIONS = { payNow: 1, payLater: 2 } as const;

const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '',
);

const resolvePayLaterDeadline = (embarkationDate?: string): Date => {
  const parsed = embarkationDate ? parseISO(embarkationDate) : null;
  if (parsed && isValid(parsed)) {
    // Hold until 7 days before embarkation, but never sooner than 3 days from now.
    const beforeTrip = addDays(parsed, -7);
    const minHold = addDays(new Date(), 3);
    return beforeTrip > minHold ? beforeTrip : minHold;
  }
  return addDays(new Date(), 3);
};

const Payment = () => {
  const { isAuthenticated, user } = useAuth0();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as PaymentLocationState | null;

  const [checkoutState, setCheckoutState] =
    useState<PaymentLocationState | null>(() => {
      if (locationState?.tripId && locationState.selectedRooms?.length) {
        return locationState;
      }
      return loadPaymentDraft();
    });

  useEffect(() => {
    if (locationState?.tripId && locationState.selectedRooms?.length) {
      setCheckoutState(locationState);
      savePaymentDraft(locationState);
    }
  }, [locationState]);

  useEffect(() => {
    if (!checkoutState?.tripId || !checkoutState?.selectedRooms?.length) {
      navigate('/', { replace: true });
    }
  }, [checkoutState, navigate]);

  const { user: userData } = useGetUser(
    user?.sub ?? '',
    isAuthenticated && !!user?.sub,
  );
  const [guest, setGuest] = useState({
    fullName: '',
    email: '',
    phone: '',
    gender: '',
  });

  const handleGuestChange = (field: keyof typeof guest, value: string) => {
    setGuest(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (userData) {
      setGuest({
        fullName: userData.fullName ?? '',
        email: userData.email ?? '',
        phone: userData.phone ?? '',
        gender: userData.gender ?? '',
      });
    }
  }, [userData]);

  const { tripBenefits, tripBenefitsLoading } = useGetTripBenefits(
    checkoutState?.tripId ?? 0,
    !!checkoutState?.tripId,
  );

  const [couponCode, setCouponCode] = useState('');
  const [appliedCouponCode, setAppliedCouponCode] = useState('');
  // Survives CheckoutForm remounts when a coupon recreates the PaymentIntent.
  const [payNowBookingId, setPayNowBookingId] = useState<number | null>(null);

  useEffect(() => {
    // New trip / room selection must not reuse a prior pay-now booking id.
    setPayNowBookingId(null);
  }, [checkoutState?.tripId, checkoutState?.selectedRooms]);

  const [selectedPaymentOption, setSelectedPaymentOption] = useState(2);
  const [paymentIntentData, setPaymentIntentData] = useState<{
    clientSecret: string;
    totalAmount: number;
    paymentIntentId: string | null;
  } | null>(null);

  const numberOfGuests = useMemo(
    () => computeNumberOfGuests(checkoutState?.selectedRooms ?? []),
    [checkoutState?.selectedRooms],
  );

  const transformSelectedRoomsToInput = (
    selectedRooms: PaymentLocationState['selectedRooms'],
  ): PaymentIntentRoomInput[] =>
    selectedRooms.map(room => ({
      id: room.typeId,
      quantity: room.numberOfRooms,
    }));

  const createPaymentIntent = useCreatePaymentIntent();

  const [paymentIntentError, setPaymentIntentError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (selectedPaymentOption === PAYMENT_OPTIONS.payLater) {
      setPaymentIntentData(null);
      setPaymentIntentError(null);
      setAppliedCouponCode('');
      return;
    }

    if (
      !checkoutState?.tripId ||
      !checkoutState?.selectedRooms?.length ||
      selectedPaymentOption !== PAYMENT_OPTIONS.payNow ||
      createPaymentIntent.isPending ||
      paymentIntentData
    ) {
      return;
    }

    const input: PaymentIntentCreateInput = {
      tripId: checkoutState.tripId,
      roomIds: transformSelectedRoomsToInput(checkoutState.selectedRooms),
      isAuthenticated,
      userId: user?.sub,
      couponCode: couponCode || null,
      boatId: checkoutState.boatId || null,
    };

    createPaymentIntent.mutate(input, {
      onSuccess: data => {
        setPaymentIntentError(null);
        setAppliedCouponCode(couponCode);
        const clientSecret = data.createPaymentIntent.clientSecret;
        setPaymentIntentData({
          clientSecret,
          totalAmount: data.createPaymentIntent.totalAmount as number,
          paymentIntentId: paymentIntentIdFromClientSecret(clientSecret),
        });
      },
      onError: error => {
        console.error('Failed to create payment intent:', error);
        setPaymentIntentError('Unable to load payment form. Please try again.');
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedPaymentOption,
    paymentIntentData,
    createPaymentIntent.isPending,
    checkoutState?.tripId,
    checkoutState?.selectedRooms,
    checkoutState?.boatId,
    isAuthenticated,
    couponCode,
  ]);

  if (!checkoutState?.tripId || !checkoutState?.selectedRooms?.length) {
    return null;
  }

  const formattedDate = format(
    resolvePayLaterDeadline(checkoutState.embarkationDate),
    'd MMMM yyyy',
  );

  const options: StripeElementsOptions = {
    clientSecret: paymentIntentData?.clientSecret ?? '',
    appearance: {
      theme: 'stripe',
    },
  };

  return (
    <div className="flex p-6 flex-col items-start space-y-6">
      <PageHeaderComponent />
      <div className="flex flex-col w-full gap-4 md:flex-row-reverse md:gap-12">
        <div className="flex w-full flex-col gap-6 md:border-[1.5px] md:rounded-3xl md:p-6">
          <TripInfoComponent
            boatName={checkoutState.boatName}
            tripDate={checkoutState.tripDate}
            routeName={checkoutState.routeName}
            numberOfDays={checkoutState.numberOfDays}
            numberOfNights={checkoutState.numberOfNights}
            numberOfDives={checkoutState.numberOfDives}
          />
          <RoomInfoComponent
            selectedRooms={checkoutState.selectedRooms}
            boatRoom={checkoutState.boatRooms}
            currency={checkoutState.currency}
            userId={userData?.userId ?? 0}
            boatId={checkoutState.boatId}
            onApplyCoupon={code => {
              // Clear the current PaymentIntent so a discounted intent is created.
              setCouponCode(code);
              if (code !== appliedCouponCode) {
                setPaymentIntentData(null);
                setPaymentIntentError(null);
              }
            }}
          />
          {tripBenefitsLoading ? (
            <div className="flex justify-center items-center">
              <Skeleton variant="rectangular" width="100%" />
            </div>
          ) : (
            <TripBenefit tripBenefit={tripBenefits} />
          )}
          <div className="w-full md:hidden">
            <Divider />
          </div>
        </div>
        <div className="flex w-full flex-col gap-4">
          <GuestDetailForm guest={guest} onGuestChange={handleGuestChange} />
          <div>
            <span className="text-lg font-bold">
              Choose your payment option
            </span>
            <div className="flex flex-col gap-4 mt-2">
              <div
                className="flex flex-col gap-2"
                onClick={() => setSelectedPaymentOption(2)}
              >
                <div className="flex items-center font-bold">
                  <input
                    type="radio"
                    name="paymentOption"
                    id="payLater"
                    value={PAYMENT_OPTIONS.payLater}
                    checked={selectedPaymentOption === PAYMENT_OPTIONS.payLater}
                  />
                  <label className="text-sm ml-2" htmlFor="payLater">
                    Pay later
                  </label>
                </div>
                <div className="flex flex-col bg-lime-100 pl-6 py-3 pr-3 rounded-xl">
                  <span className="text-sm font-bold">
                    <span className="text-green-600">RISK FREE!</span> Pay
                    nothing until {formattedDate}
                  </span>
                  <span className="text-sm text-slate-600">
                    We&apos;ll hold your spot, free of charge. Complete your
                    booking by following the payment link provided in your
                    confirmation email.
                  </span>
                </div>
              </div>
              <div
                className="flex flex-col gap-2"
                onClick={() => setSelectedPaymentOption(1)}
              >
                <div className="flex items-center font-bold">
                  <input
                    type="radio"
                    name="paymentOption"
                    id="payNow"
                    value={PAYMENT_OPTIONS.payNow}
                    checked={selectedPaymentOption === PAYMENT_OPTIONS.payNow}
                  />
                  <label className="text-sm ml-2" htmlFor="payNow">
                    Pay now
                  </label>
                </div>
                <span className="pl-6 text-sm text-slate-600">
                  You can also choose to pay now with a credit card or other
                  payment methods
                </span>
              </div>
            </div>
          </div>
          <div className="w-full">
            {createPaymentIntent.isPending && (
              <div className="flex justify-center items-center p-4">
                <Skeleton variant="rectangular" width="100%" height={200} />
              </div>
            )}
            {paymentIntentError && (
              <div className="text-red-500 text-sm p-2">
                {paymentIntentError}
              </div>
            )}
            {!createPaymentIntent.isPending &&
              (selectedPaymentOption === PAYMENT_OPTIONS.payNow
                ? paymentIntentData?.clientSecret && (
                    <Elements stripe={stripePromise} options={options}>
                      <CheckoutForm
                        tripId={checkoutState.tripId}
                        selectedRooms={checkoutState.selectedRooms}
                        email={guest.email}
                        fullName={guest.fullName}
                        gender={guest.gender}
                        phoneNumber={guest.phone}
                        paymentOption={selectedPaymentOption}
                        numberOfGuests={numberOfGuests}
                        currency={checkoutState.currency}
                        boatId={checkoutState.boatId}
                        paymentIntentId={paymentIntentData.paymentIntentId}
                        existingBookingId={payNowBookingId}
                        onBookingCreated={setPayNowBookingId}
                      />
                    </Elements>
                  )
                : selectedPaymentOption === PAYMENT_OPTIONS.payLater && (
                    <PayLaterForm
                      tripId={checkoutState.tripId}
                      boatId={checkoutState.boatId}
                      selectedRooms={checkoutState.selectedRooms}
                      email={guest.email}
                      fullName={guest.fullName}
                      gender={guest.gender}
                      phoneNumber={guest.phone}
                      paymentOption={selectedPaymentOption}
                      numberOfGuests={numberOfGuests}
                      currency={checkoutState.currency}
                      isAuthenticated={isAuthenticated}
                      userId={user?.sub}
                      couponCode={couponCode}
                    />
                  ))}
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-neutral-500 text-xs">
            <Link className="underline" to="/privacypolicy">
              Privacy policy
            </Link>
            <span aria-hidden>·</span>
            <a
              className="underline"
              href="mailto:help@santtai.com?subject=Terms%20and%20cancellation%20policy"
            >
              Terms &amp; cancellation policy
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
